import { put, call, takeLatest, fork, select } from 'redux-saga/effects'
import RootState from '../root-state'
import {
  preMakingTemplatesActionType,
  preMakingTemplatesActions,
} from './actions'
import {
  getPreMakingTemplates,
  getPreMakingTemplateDetail,
  getPreviousDayOrderCountsForTemplate,
  updatePreMakingTemplateDetail,
  makePreMakingTemplateActive,
  makePreMakingTemplateInactive,
  createPreMakingTemplate,
  deleteCustomTemplateAPI,
} from '../api'
import { auth0Actions } from '../auth0'
import { toastActions } from '../toast'

export function* fetchPreMakingTemplates(
  action: ReturnType<typeof preMakingTemplatesActions.fetchPreMakingTemplates>
) {
  try {
    yield put(auth0Actions.checkSession())
    const state: RootState = yield select()
    const token = state.auth0.auth.idToken
    const response = yield call(
      getPreMakingTemplates,
      token,
      action.payload.stationId
    )
    yield put(
      preMakingTemplatesActions.fetchPreMakingTemplatesSucceeded(
        response.preMakingTemplates,
        response.customTemplateSchedules
      )
    )
  } catch (error) {
    yield put(preMakingTemplatesActions.throwError(error))
  }
}

export function* fetchPreMakingTemplateDetail(
  action: ReturnType<
    typeof preMakingTemplatesActions.fetchPreMakingTemplateDetail
  >
) {
  try {
    yield put(auth0Actions.checkSession())
    const state: RootState = yield select()
    const token = state.auth0.auth.idToken
    const response = yield call(
      getPreMakingTemplateDetail,
      token,
      action.payload.templateId
    )
    yield put(
      preMakingTemplatesActions.fetchPreMakingTemplateDetailSucceeded(response)
    )
  } catch (error) {
    yield put(preMakingTemplatesActions.throwError(error))
  }
}

export function* watchFetchPreMakingTemplates() {
  yield takeLatest(
    preMakingTemplatesActionType.FETCH_PRE_MAKING_TEMPLATES,
    fetchPreMakingTemplates
  )
}

export function* updatePreMakingTemplateDetailSaga(
  action: ReturnType<
    typeof preMakingTemplatesActions.updatePreMakingTemplateDetail
  >
) {
  try {
    yield put(auth0Actions.checkSession())
    const state: RootState = yield select()
    const token = state.auth0.auth.idToken
    yield call(
      updatePreMakingTemplateDetail,
      token,
      action.payload.templateId,
      action.payload.name,
      action.payload.description,
      action.payload.items,
      action.payload.dates,
      action.payload.templateType
    )
    yield put(
      preMakingTemplatesActions.updatePreMakingTemplateDetailSucceeded()
    )
    // 更新後に詳細を再取得
    yield put(
      preMakingTemplatesActions.fetchPreMakingTemplateDetail(
        action.payload.templateId
      )
    )
    yield put(
      toastActions.show({
        title: '成功！',
        body: '事前調理テンプレートを更新しました',
      })
    )
  } catch (error) {
    yield put(preMakingTemplatesActions.throwError(error))
    yield put(
      toastActions.show({
        type: 'error',
        title: '失敗...',
        body: '事前調理テンプレートの更新に失敗しました',
      })
    )
  }
}

export function* watchFetchPreMakingTemplateDetail() {
  yield takeLatest(
    preMakingTemplatesActionType.FETCH_PRE_MAKING_TEMPLATE_DETAIL,
    fetchPreMakingTemplateDetail
  )
}

export function* fetchPreviousDayOrderCountsSaga(
  action: ReturnType<
    typeof preMakingTemplatesActions.fetchPreviousDayOrderCounts
  >
) {
  try {
    yield put(auth0Actions.checkSession())
    const state: RootState = yield select()
    const token = state.auth0.auth.idToken
    const response = yield call(
      getPreviousDayOrderCountsForTemplate,
      token,
      action.payload.templateId
    )
    yield put(
      preMakingTemplatesActions.fetchPreviousDayOrderCountsSucceeded(response)
    )
    if (response.hasData) {
      yield put(
        toastActions.show({
          title: '成功！',
          body: `前日(${response.previousDate})の提供杯数を読み込みました`,
        })
      )
    } else if (response.message) {
      yield put(
        toastActions.show({
          title: 'お知らせ',
          body: response.message,
        })
      )
    }
  } catch (error) {
    yield put(
      preMakingTemplatesActions.fetchPreviousDayOrderCountsFailed(error)
    )
    yield put(
      toastActions.show({
        type: 'error',
        title: '失敗...',
        body: '前日の注文数の取得に失敗しました',
      })
    )
  }
}

export function* watchFetchPreviousDayOrderCounts() {
  yield takeLatest(
    preMakingTemplatesActionType.FETCH_PREVIOUS_DAY_ORDER_COUNTS,
    fetchPreviousDayOrderCountsSaga
  )
}

export function* watchUpdatePreMakingTemplateDetail() {
  yield takeLatest(
    preMakingTemplatesActionType.UPDATE_PRE_MAKING_TEMPLATE_DETAIL,
    updatePreMakingTemplateDetailSaga
  )
}

export function* makePreMakingTemplateActiveSaga(
  action: ReturnType<
    typeof preMakingTemplatesActions.makePreMakingTemplateActive
  >
) {
  try {
    yield put(auth0Actions.checkSession())
    const state: RootState = yield select()
    const token = state.auth0.auth.idToken
    const response = yield call(
      makePreMakingTemplateActive,
      token,
      action.payload.templateId
    )
    yield put(
      preMakingTemplatesActions.makePreMakingTemplateActiveSucceeded(
        action.payload.templateId,
        response.isActive
      )
    )
    yield put(
      toastActions.show({
        title: '成功！',
        body: 'テンプレートを有効にしました',
      })
    )
  } catch (error) {
    yield put(preMakingTemplatesActions.throwError(error))
    yield put(
      toastActions.show({
        type: 'error',
        title: '失敗...',
        body: 'テンプレートの有効化に失敗しました',
      })
    )
  }
}

export function* makePreMakingTemplateInactiveSaga(
  action: ReturnType<
    typeof preMakingTemplatesActions.makePreMakingTemplateInactive
  >
) {
  try {
    yield put(auth0Actions.checkSession())
    const state: RootState = yield select()
    const token = state.auth0.auth.idToken
    const response = yield call(
      makePreMakingTemplateInactive,
      token,
      action.payload.templateId
    )
    yield put(
      preMakingTemplatesActions.makePreMakingTemplateInactiveSucceeded(
        action.payload.templateId,
        response.isActive
      )
    )
    yield put(
      toastActions.show({
        title: '成功！',
        body: 'テンプレートを無効にしました',
      })
    )
  } catch (error) {
    yield put(preMakingTemplatesActions.throwError(error))
    yield put(
      toastActions.show({
        type: 'error',
        title: '失敗...',
        body: 'テンプレートの無効化に失敗しました',
      })
    )
  }
}

export function* watchMakePreMakingTemplateActive() {
  yield takeLatest(
    preMakingTemplatesActionType.MAKE_PRE_MAKING_TEMPLATE_ACTIVE,
    makePreMakingTemplateActiveSaga
  )
}

export function* watchMakePreMakingTemplateInactive() {
  yield takeLatest(
    preMakingTemplatesActionType.MAKE_PRE_MAKING_TEMPLATE_INACTIVE,
    makePreMakingTemplateInactiveSaga
  )
}

export function* createPreMakingTemplateSaga(
  action: ReturnType<typeof preMakingTemplatesActions.createPreMakingTemplate>
) {
  try {
    yield put(auth0Actions.checkSession())
    const state: RootState = yield select()
    const token = state.auth0.auth.idToken
    const response = yield call(
      createPreMakingTemplate,
      token,
      action.payload.stationId,
      action.payload.name,
      action.payload.description,
      action.payload.templateType
    )
    yield put(
      preMakingTemplatesActions.createPreMakingTemplateSucceeded(response)
    )
    yield put(
      toastActions.show({
        title: '成功！',
        body: 'カスタムテンプレートを作成しました',
      })
    )
    // モーダルを閉じる
    yield put({ type: 'MODAL_HIDE' })
    // テンプレート一覧を再取得
    yield put(
      preMakingTemplatesActions.fetchPreMakingTemplates(
        action.payload.stationId
      )
    )
  } catch (error) {
    yield put(preMakingTemplatesActions.throwError(error))
    yield put(
      toastActions.show({
        type: 'error',
        title: '失敗...',
        body: 'カスタムテンプレートの作成に失敗しました',
      })
    )
  }
}

export function* watchCreatePreMakingTemplate() {
  yield takeLatest(
    preMakingTemplatesActionType.CREATE_PRE_MAKING_TEMPLATE,
    createPreMakingTemplateSaga
  )
}

export function* deleteCustomTemplateWorker(
  action: ReturnType<
    typeof preMakingTemplatesActions.deleteCustomTemplateRequest
  >
) {
  try {
    // 1. 確認ダイアログ表示
    const confirmed = yield call(() =>
      window.confirm(
        'テンプレートを削除しますか？\nこの操作は取り消すことができません。'
      )
    )

    if (confirmed) {
      // 2. 認証チェックとAPI呼び出し
      yield put(auth0Actions.checkSession())
      const state: RootState = yield select()
      const token = state.auth0.auth.idToken

      yield call(deleteCustomTemplateAPI, token, action.payload.templateId)

      // 3. 成功時の状態更新
      yield put(
        preMakingTemplatesActions.deleteCustomTemplateSuccess(
          action.payload.templateId
        )
      )

      // 4. 成功メッセージ表示
      yield put(
        toastActions.show({
          title: '成功！',
          body: 'カスタムテンプレートを削除しました',
        })
      )

      // 5. テンプレート一覧を再取得
      const currentStationId = state.preMakingTemplates.selectedStationId
      if (currentStationId) {
        yield put(
          preMakingTemplatesActions.fetchPreMakingTemplates(currentStationId)
        )
      }

      // 6. 事前調理画面へ遷移
      if (typeof window !== 'undefined') {
        window.location.assign('/pre_making')
      }
    } else {
      // キャンセル時の処理
      yield put(preMakingTemplatesActions.deleteCustomTemplateCancelled())
    }
  } catch (error) {
    // エラーハンドリング
    yield put(preMakingTemplatesActions.deleteCustomTemplateFailure(error))
    yield put(
      toastActions.show({
        type: 'error',
        title: '失敗...',
        body: error.message || 'カスタムテンプレートの削除に失敗しました',
      })
    )
  }
}

export function* watchDeleteCustomTemplate() {
  yield takeLatest(
    preMakingTemplatesActionType.DELETE_CUSTOM_TEMPLATE_REQUEST,
    deleteCustomTemplateWorker
  )
}

export const preMakingTemplatesSagas = [
  fork(watchFetchPreMakingTemplates),
  fork(watchFetchPreMakingTemplateDetail),
  fork(watchFetchPreviousDayOrderCounts),
  fork(watchUpdatePreMakingTemplateDetail),
  fork(watchMakePreMakingTemplateActive),
  fork(watchMakePreMakingTemplateInactive),
  fork(watchCreatePreMakingTemplate),
  fork(watchDeleteCustomTemplate),
]
